import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { RangeDayClasses, dayOf, rangeDayClasses } from '../date-range-filter/date-range-filter.utils';

/** A slice of the day shown as one tab of time buttons. Hours are 24h, end exclusive. */
export interface PickerPeriod {
  key: 'morning' | 'afternoon' | 'evening' | 'night';
  label: string;
  from: number;
  to: number;
}

/** One time button: the 24h value it sets and the 12h label it shows. */
export interface PickerTimeSlot {
  h24: number;
  minute: number;
  label: string;
}

// Compact picker: time is picked from half-hour buttons grouped by part of the day, with
// an exact-time row for anything in between. Night runs past midnight so a booking that
// ends at 1 AM has a button.
export const PICKER_PERIODS: PickerPeriod[] = [
  { key: 'morning', label: 'COMMON.PICKER_MORNING', from: 6, to: 12 },
  { key: 'afternoon', label: 'COMMON.PICKER_AFTERNOON', from: 12, to: 18 },
  { key: 'evening', label: 'COMMON.PICKER_EVENING', from: 18, to: 24 },
  { key: 'night', label: 'COMMON.PICKER_NIGHT', from: 0, to: 6 },
];
export const PICKER_SLOT_MINUTES = 30;

@Component({
    selector: 'app-datetime-picker',
    templateUrl: './datetime-picker.component.html',
    styleUrls: ['./datetime-picker.component.scss'],
    standalone: false
})
export class DateTimePickerComponent implements OnInit {
  @Input() initialDate: Date = new Date();
  @Output() confirm = new EventEmitter<Date>();
  @Output() discard = new EventEmitter<void>();

  @ViewChild('confirmBtn') confirmBtn?: ElementRef<HTMLButtonElement>;

  MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  HOURS_12 = ["12","01","02","03","04","05","06","07","08","09","10","11"];
  MINUTES = ["00","05","10","15","20","25","30","35","40","45","50","55"];
  readonly PERIODS = PICKER_PERIODS;

  viewYear!: number;
  viewMonth!: number;
  selectedDate!: number;
  selectedHour!: string;
  selectedMinute!: string;
  amPm!: string;

  /** The tab of time buttons on show. */
  period: PickerPeriod = PICKER_PERIODS[1];
  /** The hour / minute / AM-PM row for a time that is not on a button. */
  showExactTime = false;

  // Card #204: opt-in start → end flow (data.range === true). Confirming the start step
  // keeps the dialog open on the end step; the dialog then closes with { start, end }.
  // Without data.range the picker closes with a single Date.
  rangeMode = false;
  step: 'start' | 'end' = 'start';
  rangeStart: Date | null = null;
  initialEndDate: Date | null = null;
  /** The end the user had set on the end step, kept across a trip back to Start. */
  private pendingEnd: Date | null = null;
  rangeError = '';
  private endStepShownAt = 0;
  private static readonly END_STEP_SETTLE_MS = 700;

  // Card #358: the End field's picker (single date) is told the booking's start
  // (data.highlightStart) so it can show the whole range, not just the end being edited.
  highlightStart: Date | null = null;

  /** The day being edited, fixed at open time: its week always stays visible in `weeks`. */
  private readonly anchorDay: { y: number; m: number; d: number };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<DateTimePickerComponent>
  ) {
    if (data && data.initialDate) {
      this.initialDate = data.initialDate;
    }
    if (data?.range === true) {
      this.rangeMode = true;
      const end = data.initialEndDate ? new Date(data.initialEndDate) : null;
      this.initialEndDate = end && !isNaN(end.getTime()) ? end : null;
    } else if (data?.highlightStart) {
      const start = new Date(data.highlightStart);
      this.highlightStart = !isNaN(start.getTime()) ? start : null;
    }
    this.anchorDay = {
      y: this.initialDate.getFullYear(),
      m: this.initialDate.getMonth(),
      d: this.initialDate.getDate(),
    };
  }

  ngOnInit(): void {
    this.loadDate(this.initialDate || new Date());
  }

  /** Point the calendar and the time at a date. */
  private loadDate(date: Date): void {
    this.viewYear = date.getFullYear();
    this.viewMonth = date.getMonth();
    this.selectedDate = date.getDate();
    const minutes = date.getMinutes();
    const minute = this.MINUTES.reduce((prev, curr) =>
      Math.abs(parseInt(curr, 10) - minutes) < Math.abs(parseInt(prev, 10) - minutes) ? curr : prev
    );
    this.setTime(date.getHours(), parseInt(minute, 10));
    this.period = this.periodOf(date.getHours());
    this.showExactTime = false;
    this.rangeError = '';
  }

  /** Set the selected time from a 24h hour and a minute. */
  private setTime(h24: number, minute: number): void {
    this.amPm = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    this.selectedHour = h12.toString().padStart(2, '0');
    this.selectedMinute = minute.toString().padStart(2, '0');
  }

  /** The selected time as a 24h hour. */
  get selectedHour24(): number {
    const h = parseInt(this.selectedHour, 10) % 12;
    return this.amPm === 'PM' ? h + 12 : h;
  }

  periodOf(h24: number): PickerPeriod {
    return PICKER_PERIODS.find(p => h24 >= p.from && h24 < p.to) ?? PICKER_PERIODS[1];
  }

  // Keep the selected day inside the viewed month (31 → 30 when moving to a 30-day month),
  // so confirming can never roll over into the next month.
  private clampSelectedDay(): void {
    const last = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    if (this.selectedDate > last) this.selectedDate = last;
  }

  get daysInMonth(): number[] {
    const count = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  get firstDayOffset(): number[] {
    const offset = new Date(this.viewYear, this.viewMonth, 1).getDay();
    return Array.from({ length: offset });
  }

  /**
   * The month as rows of seven (null = a blank before the 1st or after the last day).
   * In the current month, rows that are wholly in the past are left out so the calendar
   * stays short, unless a row holds the day being edited (fixed at open time, never the
   * day merely clicked since - card #596 round 1 made rows disappear as the user browsed
   * other weeks) or a booking end, which must stay visible when an old booking is edited.
   */
  get weeks(): (number | null)[][] {
    const cells: (number | null)[] = [...this.firstDayOffset.map(() => null), ...this.daysInMonth];
    while (cells.length % 7) cells.push(null);
    const rows: (number | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    const today = new Date();
    if (this.viewYear !== today.getFullYear() || this.viewMonth !== today.getMonth()) return rows;
    const ends = this.rangeEnds;
    const inAnchorMonth = this.anchorDay.y === this.viewYear && this.anchorDay.m === this.viewMonth;
    const keep = (day: number) =>
      (inAnchorMonth && day === this.anchorDay.d) ||
      [ends?.start, ends?.end].some(e => !!e && e.getFullYear() === this.viewYear &&
        e.getMonth() === this.viewMonth && e.getDate() === day);
    const visible = rows.filter(row => row.some(d => d !== null && (!this.isPast(d) || keep(d))));
    return visible.length ? visible : rows;
  }

  isToday(day: number): boolean {
    const today = new Date();
    return day === today.getDate() && this.viewMonth === today.getMonth() && this.viewYear === today.getFullYear();
  }

  isPast(day: number): boolean {
    const today = new Date();
    const d = new Date(this.viewYear, this.viewMonth, day);
    today.setHours(0, 0, 0, 0);
    return d < today;
  }

  prevMonth(): void {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear--;
    } else {
      this.viewMonth--;
    }
    this.clampSelectedDay();
    this.rangeError = '';
  }

  nextMonth(): void {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear++;
    } else {
      this.viewMonth++;
    }
    this.clampSelectedDay();
    this.rangeError = '';
  }

  get headerDate(): Date {
    return new Date(this.viewYear, this.viewMonth, this.selectedDate);
  }

  /** The date and time being picked, as a Date. */
  get pickedDate(): Date {
    return new Date(this.viewYear, this.viewMonth, this.selectedDate, this.selectedHour24, parseInt(this.selectedMinute, 10));
  }

  get headerTimeText(): string {
    return `${parseInt(this.selectedHour, 10)}:${this.selectedMinute} ${this.amPm}`;
  }

  /** "Sat 24 Oct · 5:00 PM", with the day and month names translated like the calendar's. */
  formatDateTime(d: Date | null): string {
    if (!d) return '';
    const day = this.DAYS[d.getDay()];
    const month = this.MONTHS[d.getMonth()];
    const h12 = d.getHours() % 12 === 0 ? 12 : d.getHours() % 12;
    const time = `${h12}:${d.getMinutes().toString().padStart(2, '0')} ${d.getHours() >= 12 ? 'PM' : 'AM'}`;
    return `${this.getTranslatedText('DAYS.' + day) || day} ${d.getDate()} ${this.getTranslatedText('MONTHS.' + month) || month} · ${time}`;
  }

  /** The two values shown in the Start / End tabs of the range flow. */
  get tabStart(): Date | null {
    return this.step === 'end' ? this.rangeStart : this.pickedDate;
  }

  get tabEnd(): Date | null {
    if (this.step === 'end') return this.pickedDate;
    // card #658: prefer the end the user already picked (backToStart() keeps it as
    // pendingEnd) over the pre-picker default, the same order confirmRangeStep resolves it in.
    return this.pendingEnd && this.pendingEnd > this.pickedDate ? this.pendingEnd
      : this.initialEndDate && this.initialEndDate > this.pickedDate ? this.initialEndDate : null;
  }

  /** The booking start the end is measured from, if any. */
  private get measureFrom(): Date | null {
    if (this.rangeMode) return this.step === 'end' ? this.rangeStart : null;
    return this.highlightStart;
  }

  /** "8 h", "1 d 2 h", "30 min" — how long the booking would last if it ended at `end`. */
  durationLabel(end: Date): string {
    const from = this.measureFrom;
    if (!from) return '';
    const mins = Math.round((end.getTime() - from.getTime()) / 60000);
    if (mins <= 0) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h >= 24) {
      const day = this.getTranslatedText('COMMON.PICKER_DAY_SHORT') || 'd';
      return [`${Math.floor(h / 24)} ${day}`, h % 24 ? `${h % 24} h` : '', m ? `${m} min` : ''].filter(Boolean).join(' ');
    }
    if (h === 0) return `${m} min`;
    return m ? `${h} h ${m}` : `${h} h`;
  }

  get pickedDuration(): string {
    return this.durationLabel(this.pickedDate);
  }

  /** The half-hour buttons of the tab on show. */
  get timeSlots(): PickerTimeSlot[] {
    const slots: PickerTimeSlot[] = [];
    for (let h = this.period.from; h < this.period.to; h++) {
      for (let m = 0; m < 60; m += PICKER_SLOT_MINUTES) {
        const h12 = h % 12 === 0 ? 12 : h % 12;
        slots.push({ h24: h, minute: m, label: `${h12}:${m.toString().padStart(2, '0')}` });
      }
    }
    return slots;
  }

  slotDate(slot: PickerTimeSlot): Date {
    return new Date(this.viewYear, this.viewMonth, this.selectedDate, slot.h24, slot.minute);
  }

  isSlotSelected(slot: PickerTimeSlot): boolean {
    return slot.h24 === this.selectedHour24 && slot.minute === parseInt(this.selectedMinute, 10);
  }

  /** On the end step a time at or before the chosen start can't be picked. */
  isSlotBlocked(slot: PickerTimeSlot): boolean {
    return this.rangeMode && this.step === 'end' && !!this.rangeStart && this.slotDate(slot) <= this.rangeStart;
  }

  selectPeriod(p: PickerPeriod): void {
    this.period = p;
  }

  /**
   * A time button. On the start step of the range flow it also moves on to the end, the
   * same as "Next". A second tap of a double-tap lands on the end step's button in the same
   * place, so the end step ignores buttons for a moment after it appears.
   */
  selectSlot(slot: PickerTimeSlot): void {
    if (this.isSlotBlocked(slot)) return;
    if (this.rangeMode && this.step === 'end' &&
        Date.now() - this.endStepShownAt < DateTimePickerComponent.END_STEP_SETTLE_MS) {
      return;
    }
    this.setTime(slot.h24, slot.minute);
    this.rangeError = '';
    if (this.rangeMode && this.step === 'start') {
      this.confirmRangeStep(this.pickedDate);
    }
  }

  /** The exact-time row changed (hour, minute or AM/PM). */
  onExactTimeChange(): void {
    this.period = this.periodOf(this.selectedHour24);
    // The time buttons can't pick an end at or before the start; say so at once for a typed one
    // too, rather than only when Select is pressed.
    this.rangeError = this.rangeMode && this.step === 'end' && !!this.rangeStart && this.pickedDate <= this.rangeStart
      ? this.getTranslatedText('COMMON.RANGE_END_BEFORE_START') || 'The end must be after the start.'
      : '';
  }

  onConfirm(event?: MouseEvent): void {
    // A repeat click or tap on "Next: end date" must not also confirm the end step. The browser
    // click count catches a double-click; touch screens count two taps half a second apart as
    // separate clicks, so the end step also ignores Select for a moment after it appears.
    if (this.rangeMode && this.step === 'end' &&
        ((event?.detail ?? 0) > 1 || Date.now() - this.endStepShownAt < DateTimePickerComponent.END_STEP_SETTLE_MS)) {
      return;
    }
    const finalDate = this.pickedDate;
    if (isNaN(finalDate.getTime())) {
      console.error('DatePicker: invalid date or time');
      return;
    }
    if (this.rangeMode) {
      this.confirmRangeStep(finalDate);
      return;
    }
    this.confirm.emit(finalDate);
    this.dialogRef.close(finalDate);
  }

  private confirmRangeStep(picked: Date): void {
    if (this.step === 'start') {
      this.rangeStart = picked;
      this.step = 'end';
      this.endStepShownAt = Date.now();
      this.rangeError = '';
      // Keep the end the user had already set before a trip back to Start; otherwise a
      // valid existing end; otherwise default to the start day at 11:59 PM, the same
      // default the booking fields already apply when only a start is picked.
      let end = this.pendingEnd && this.pendingEnd > picked ? this.pendingEnd
        : this.initialEndDate && this.initialEndDate > picked ? this.initialEndDate : null;
      this.pendingEnd = null;
      if (!end) {
        end = new Date(picked);
        end.setHours(23, 59, 0, 0);
        if (end <= picked) end = new Date(picked.getTime() + 5 * 60 * 1000);
      }
      this.loadDate(end);
      return;
    }
    if (!this.rangeStart || picked <= this.rangeStart) {
      this.rangeError = this.getTranslatedText('COMMON.RANGE_END_BEFORE_START') || 'The end must be after the start.';
      return;
    }
    this.dialogRef.close({ start: this.rangeStart, end: picked });
  }

  /** From the end step, go back and change the start. */
  backToStart(): void {
    if (!this.rangeStart) return;
    // card #596: remember the end the user has configured so far, so returning to End
    // (goToStep('end') / onConfirm) restores it instead of re-deriving the default.
    this.pendingEnd = this.pickedDate;
    this.step = 'start';
    this.rangeError = '';
    this.loadDate(this.rangeStart);
    this.confirmBtn?.nativeElement?.focus();
  }

  /** The Start / End tabs: Start goes back, End moves on as "Next" does. */
  goToStep(target: 'start' | 'end'): void {
    if (target === this.step) return;
    if (target === 'start') this.backToStart();
    else this.onConfirm();
  }

  /**
   * Card #358: the two ends of the booking to mark on the calendar, or null when the
   * picker has no range to show (it then marks only the selected day, as before).
   * The day being edited follows the selection, so the range moves as days are clicked.
   */
  get rangeEnds(): { start: Date | null; end: Date | null } | null {
    const picked = this.headerDate;
    if (this.rangeMode) {
      if (this.step === 'end') return { start: this.rangeStart, end: picked };
      // Start step: an end before the picked start is dropped on "Next", so don't show it.
      const end = this.initialEndDate && dayOf(this.initialEndDate) >= dayOf(picked) ? this.initialEndDate : null;
      return { start: picked, end };
    }
    if (this.highlightStart) {
      // End field: an end clicked before the start is not a range, so don't draw one
      // backwards (the start would look like the end); mark just the clicked day.
      return dayOf(picked) >= dayOf(this.highlightStart)
        ? { start: this.highlightStart, end: picked }
        : { start: picked, end: null };
    }
    return null;
  }

  /** Card #358: range classes for a day cell, styled like the date range filter's calendar. */
  dayRangeClasses(day: number, ends = this.rangeEnds): RangeDayClasses | null {
    if (!ends) return null;
    return rangeDayClasses(new Date(this.viewYear, this.viewMonth, day), ends.start, ends.end);
  }

  /** On the end step, days before the chosen start can't be picked. */
  isBeforeStart(day: number): boolean {
    if (!this.rangeMode || this.step !== 'end' || !this.rangeStart) return false;
    const startDay = new Date(this.rangeStart.getFullYear(), this.rangeStart.getMonth(), this.rangeStart.getDate());
    return new Date(this.viewYear, this.viewMonth, day) < startDay;
  }

  selectDay(day: number): void {
    if (this.isBeforeStart(day)) return;
    this.selectedDate = day;
    this.rangeError = '';
  }

  onDiscard(): void {
    this.discard.emit();
    this.dialogRef.close();
  }

  getTranslatedText(key: string): string {
    const translation = this.translateService.instant(key);
    return translation !== key ? translation : '';
  }
}
